/**
 * For master admins, once the page has met a FlatBuffer schema that is not its
 * own: every such schema, how it resolved, and the last frames of those schemas, each shown as
 * JSON read with the schema it was written with (`flatbuffers/discovery/frameJson.ts`),
 * enum values by name.
 */

import React, { useEffect, useState } from "react";
import { Button, Table, Tag } from "antd";
import {
  schemaSkewSnapshot,
  skewFramesSnapshot,
  type SkewFrame,
  type SkewObservation,
} from "../../../flatbuffers/schemaSkew";
import useSchemaSkew from "../../../flatbuffers/useSchemaSkew";
import frameJson from "../../../flatbuffers/discovery/frameJson";
import GETJwtToken from "../../../redux/authentication/authentication";

/** Reads one kept frame as JSON, or says why it cannot be. */
export type FrameJsonLoader = (frame: SkewFrame) => Promise<{ json: string } | { error: string }>;

const loadFromServer: FrameJsonLoader = async (frame) =>
  frameJson(
    process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "",
    frame,
    await GETJwtToken().catch(() => undefined),
  );

const READ_COLOR: Record<string, string> = { all: "green", except: "gold", none: "red" };

const columns = [
  { title: "Where", dataIndex: "channel", key: "channel" },
  { title: "Type", dataIndex: "typeName", key: "typeName" },
  {
    title: "Schema",
    dataIndex: "schema",
    key: "schema",
    render: (schema: string) => <code title={schema}>{schema.slice(0, 12)}</code>,
  },
  { title: "Outcome", dataIndex: "outcome", key: "outcome" },
  { title: "Met", dataIndex: "count", key: "count" },
  { title: "Provenance", dataIndex: "provenance", key: "provenance" },
  { title: "Signature", dataIndex: "signature", key: "signature" },
];

function SchemaInspector({ loadJson = loadFromServer }: { loadJson?: FrameJsonLoader }) {
  const observations = useSchemaSkew(schemaSkewSnapshot);
  const frames = useSchemaSkew(skewFramesSnapshot);
  const [selected, setSelected] = useState<SkewFrame | null>(null);
  const [shown, setShown] = useState<string>("");

  useEffect(() => {
    let current = true;
    if (selected === null) {
      setShown("");
      return undefined;
    }
    setShown("Reading the frame with its schema…");
    loadJson(selected).then((result) => {
      if (current) {
        setShown("json" in result ? result.json : `Cannot show this frame: ${result.error}`);
      }
    });
    return () => {
      current = false;
    };
  }, [selected, loadJson]);

  if (observations.length === 0) {
    return null;
  }

  return (
    <section aria-label="FlatBuffer schema inspector" style={{ marginTop: 24 }}>
      <h3>FlatBuffer schemas this page was not built with</h3>
      <Table<SkewObservation>
        size="small"
        pagination={false}
        columns={columns}
        dataSource={observations.map((seen) => ({
          ...seen,
          key: `${seen.channel}|${seen.schema}|${seen.typeName}`,
        }))}
      />
      {frames.length > 0 && (
        <>
          <h4 style={{ marginTop: 16 }}>Last frames of those schemas</h4>
          <ul style={{ listStyle: "none", padding: 0 }}>
            {[...frames].reverse().map((frame) => (
              <li key={`${frame.receivedAt}|${frame.schema}`}>
                <Button
                  type="link"
                  size="small"
                  aria-pressed={selected === frame}
                  onClick={() => setSelected(frame)}
                >
                  {new Date(frame.receivedAt).toLocaleTimeString()} {frame.typeName || "?"}{" "}
                  <code>{frame.schema.slice(0, 8)}</code>
                </Button>{" "}
                <Tag color={READ_COLOR[frame.read]}>{frame.read}</Tag>
              </li>
            ))}
          </ul>
          {shown !== "" && (
            <pre data-testid="schema-inspector-json" style={{ maxHeight: 400, overflow: "auto" }}>
              {shown}
            </pre>
          )}
        </>
      )}
    </section>
  );
}

export default SchemaInspector;
